import { ORGANIZATION_ROUTES } from "@pages/organizations/routes.tsx";
import { PERSONAS_ROUTES } from "../components/personas/routes";
import { RouteObject, useSearchParams } from "react-router-dom";
import { UniversalLayout } from "../components/PageContainer";
import { PermissionedRoutes, ValidatedRoutes } from "../components/PermissionedRoutes";
import { canViewGuard } from "../hooks/permissions/useGuardEnabled.ts";
import { ACH_RETRY_ROUTES } from "./ach_retry/routes";
import { APPLICATION_ROUTES } from "./applications/routes";
import { AO_ACH_MANAGER_ROUTES } from "./ao_ach_manager/routes";
import { BANKING_ACH_MANAGER_ROUTES } from "./banking_ach_manager/routes";
import { Dashboard } from "./dashboard/Dashboard";
import { FEDNOW_MANAGER_ROUTES } from "./fednow_manager/routes";
import { MARKETING_ROUTES } from "./marketing/Marketing";
import { NAF_ROUTES, NAF_RUN_ROUTE } from "./naf/routes";
import { OAUTH_APP_ROUTES } from "./oauth_apps/routes";
import { FUNCTIONS_ROUTES } from "./narmi_functions/routes";
import { GUIDES_ROUTES } from "./guides/routes";
import { CONVERSION_PORTAL_ROUTES } from "./conversion_portal/routes";
import { CONVERSIONS_ROUTES } from "./conversions/routes";
import { DECONVERSIONS_ROUTES } from "./deconversions/routes";
import { PASSTHRU_ACH_MANAGER_ROUTES } from "./passthru_ach_manager/routes";
import { PAYMENTS_MANAGER_ROUTES } from "./payments_manager/routes";
import { PRODUCT_LIST_MANAGER_ROUTES } from "./product_list_manager/routes";
import { PRODUCT_LIST_MANAGER_V2_ROUTES } from "./product_list_manager_v2/routes";
import { SECURITY_ROUTES } from "./profile/routes";
import { RISK_MANAGER_ROUTES } from "./risk_manager/routes";
import { ROLES_ROUTES } from "./roles/routes";
import { STAFF_ROUTES } from "./staff/routes";
import { TRANSLATIONS_ROUTES } from "./translations/routes";
import { USER_ROUTES } from "./users/routes";
import { WIRE_MANAGER_ROUTES } from "./wire_manager/routes";
import { ErrorBoundary } from "./errors/ErrorBoundary";
import { MESSAGES_ROUTES } from "./messages/routes";
import { CONFIGURATION_ROUTES } from "./configuration/routes";
import { SURVEY_ROUTES } from "./survey/routes";
import { AUDIENCES_ROUTES } from "./audiences/routes";
import { INTERNATIONAL_WIRE_ROUTES } from "./intl_wire_manager/routes";
import { REPORTS_ROUTES } from "./reports/routes";
import { WIRE_FX_RATES_ROUTES } from "./wire_fx_rates/routes";
import { InstitutionContextProvider } from "@hooks/institutionContext";
import { SessionGuard } from "@components/SessionGuard";
import { NeedHelpButton, UnauthenticatedHeader } from "@components/UnauthenticatedHeader";
import { AuthenticatedRoute, UnauthenticatedRoute } from "@components/AuthGuards";

export const ROUTES: RouteObject[] = [
  // Unauthenticated routes
  {
    element: <UnauthenticatedRoute />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: "/login",
        async lazy() {
          const { LoginPage } = await import("./login/LoginPage");
          return {
            element: (
              <>
                <UnauthenticatedHeader rightContent={<NeedHelpButton />} />
                <LoginPage />
              </>
            ),
          };
        },
      },
    ],
  },
  // Authenticated routes
  {
    element: <AuthenticatedRoute />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: (
          <InstitutionContextProvider>
            <SessionGuard>
              <UniversalLayout />
            </SessionGuard>
          </InstitutionContextProvider>
        ),
        children: [
          {
            path: "",
            index: true,
            // because the dashboard is such a common page, we want to load it eagerly.
            Component: Dashboard,
          },
          {
            path: "inbox",
            children: [
              {
                index: true,
                async lazy() {
                  const { Inbox } = await import("./inbox/Inbox");
                  return { element: <Inbox /> };
                },
              },
            ],
          },
          {
            path: "ach_manager",
            children: [
              {
                path: "ao",
                element: (
                  <ValidatedRoutes
                    validate={(userConfiguration) => {
                      const { isFeatureEnabled, permissions } = userConfiguration;

                      return (
                        isFeatureEnabled("new_account_opening") &&
                        isFeatureEnabled("ach_manager_display", "V2_AO") &&
                        permissions.includes("indigo.view_achapplicationtransaction")
                      );
                    }}
                  />
                ),
                children: AO_ACH_MANAGER_ROUTES,
              },
              {
                path: "db",
                element: (
                  <ValidatedRoutes
                    validate={(userConfiguration) => {
                      const { isFeatureEnabled, permissions } = userConfiguration;

                      return (
                        isFeatureEnabled("online_banking") &&
                        permissions.includes("indigo.view_achtransaction")
                      );
                    }}
                  />
                ),
                children: BANKING_ACH_MANAGER_ROUTES,
              },
              {
                path: "payments",
                element: (
                  <PermissionedRoutes
                    requiredFeatures={["ach_payments_staff_management"]}
                    requiredPermissions={["indigo.view_achtransaction"]}
                  />
                ),
                children: PAYMENTS_MANAGER_ROUTES,
              },
              {
                path: "passthru",
                element: (
                  <PermissionedRoutes
                    requiredFeatures={["ach_nacha_passthrough"]}
                    requiredPermissions={["indigo.view_nachapassthroughsubmission"]}
                  />
                ),
                children: PASSTHRU_ACH_MANAGER_ROUTES,
              },
              {
                path: "retry",
                element: (
                  <ValidatedRoutes
                    validate={(userConfiguration) => {
                      const { isFeatureEnabled, permissions } = userConfiguration;
                      return (
                        isFeatureEnabled("retry_nacha_upload") &&
                        (permissions.includes("indigo.process_achtransaction") ||
                          permissions.includes("indigo.process_achapplicationtransaction"))
                      );
                    }}
                  />
                ),
                children: ACH_RETRY_ROUTES,
              },
            ],
          },
          {
            path: "applications",
            element: (
              <PermissionedRoutes
                requiredPermissions={["indigo.view_accountapplicationapplicants"]}
                requiredFeatures={["new_account_opening"]}
              />
            ),
            children: APPLICATION_ROUTES,
          },
          NAF_RUN_ROUTE,
          {
            path: "naf",
            element: (
              <ValidatedRoutes
                validate={(userConfiguration) => {
                  const { permissions } = userConfiguration;
                  return (
                    permissions.includes("public_api.list_oauth_applications") ||
                    permissions.includes("public_api.list_own_oauth_applications")
                  );
                }}
              />
            ),
            children: NAF_ROUTES,
          },
          {
            path: "oauth_apps",
            element: (
              <ValidatedRoutes
                validate={({ permissions }) =>
                  permissions.includes("public_api.list_oauth_applications") ||
                  permissions.includes("public_api.list_own_oauth_applications")
                }
              />
            ),
            children: OAUTH_APP_ROUTES,
          },
          {
            path: "functions",
            children: FUNCTIONS_ROUTES,
          },
          {
            path: "messages",
            element: <PermissionedRoutes requiredPermissions={["indigo.view_message"]} />,
            children: MESSAGES_ROUTES,
          },
          {
            path: "promotions",
            children: MARKETING_ROUTES,
          },
          {
            path: "users",
            element: <PermissionedRoutes requiredPermissions={["auth.view_user"]} />,
            children: USER_ROUTES,
          },
          {
            path: "organizations",
            element: (
              <PermissionedRoutes
                requiredPermissions={["auth.view_user", "indigo.view_organization"]}
                requiredFeatures={["business_banking", "organization_profile"]}
              />
            ),
            children: ORGANIZATION_ROUTES,
          },
          {
            path: "personas",
            element: (
              <PermissionedRoutes
                requiredPermissions={["indigo.view_persona"]}
                requiredFeatures={["personas"]}
              />
            ),
            children: PERSONAS_ROUTES,
          },
          {
            path: "security",
            children: SECURITY_ROUTES,
          },
          {
            path: "roles",
            element: <PermissionedRoutes requiredPermissions={["backoffice.view_group"]} />,
            children: ROLES_ROUTES,
          },
          {
            path: "risk",
            element: <PermissionedRoutes requiredPermissions={["backoffice.view_workflowrule"]} />,
            children: RISK_MANAGER_ROUTES,
          },
          {
            path: "international_wires",
            element: (
              <PermissionedRoutes
                requiredFeatures={["international_wires_staff_management"]}
                requiredPermissions={["indigo.view_swiftwiretransaction"]}
              />
            ),
            children: INTERNATIONAL_WIRE_ROUTES,
          },
          {
            path: "wire_manager",
            element: <PermissionedRoutes requiredPermissions={["indigo.view_wiretransaction"]} />,
            children: WIRE_MANAGER_ROUTES,
          },
          {
            element: (
              <ValidatedRoutes
                validate={(userConfiguration) =>
                  userConfiguration.permissions.includes(
                    "indigo.change_foreignopenidconnectapplication"
                  ) ||
                  userConfiguration.permissions.includes(
                    "indigo.delete_foreignopenidconnectapplication"
                  )
                }
              />
            ),
            children: [
              {
                path: "sso",
                async lazy() {
                  const { SSOConfiguration } = await import("./sso_configuration/SSOConfiguration");
                  return { element: <SSOConfiguration /> };
                },
              },
            ],
          },
          {
            path: "staff",
            children: STAFF_ROUTES,
          },
          {
            path: "notification_history",
            async lazy() {
              const { NotificationHistory } = await import(
                "./notification_history/NotificationHistory"
              );
              return { element: <NotificationHistory /> };
            },
          },
          {
            path: "narmiadmin",
            element: (
              <iframe
                src="/admin/"
                style={{ width: "100%", height: "100%", border: 0, display: "block" }}
              />
            ),
          },
          {
            element: <PermissionedRoutes requiredRoles={["narmi"]} />,
            children: [
              {
                path: "test_connectivity",
                async lazy() {
                  const { TestCoreConnectivity } = await import(
                    "./core_connectivity/TestCoreConnectivity"
                  );
                  return { element: <TestCoreConnectivity /> };
                },
              },
            ],
          },
          {
            path: "product_list_manager",
            children: PRODUCT_LIST_MANAGER_ROUTES,
          },
          {
            path: "product_list_manager_v2",
            children: PRODUCT_LIST_MANAGER_V2_ROUTES,
          },
          {
            path: "guides",
            children: GUIDES_ROUTES,
          },
          {
            path: "conversions",
            children: CONVERSIONS_ROUTES,
          },
          {
            path: "conversion_portal",
            children: CONVERSION_PORTAL_ROUTES,
          },
          {
            path: "deconversions",
            children: DECONVERSIONS_ROUTES,
          },
          {
            path: "translations",
            children: TRANSLATIONS_ROUTES,
          },
          {
            path: "fednow",
            children: FEDNOW_MANAGER_ROUTES,
          },
          {
            path: "configuration",
            children: CONFIGURATION_ROUTES,
          },
          {
            path: "preburner",
            element: (
              <PermissionedRoutes requiredRoles={["narmi"]} requiredFeatures={["preburner"]} />
            ),
            children: [
              {
                index: true,
                async lazy() {
                  const { PreburnerPage } = await import("./preburner/PreburnerPage");
                  return { element: <PreburnerPage /> };
                },
              },
            ],
          },
          {
            path: "survey",
            children: SURVEY_ROUTES,
            element: <PermissionedRoutes requiredPermissions={["indigo.view_survey"]} />,
          },
          {
            path: "audiences",
            children: AUDIENCES_ROUTES,
            element: <PermissionedRoutes requiredFeatures={["audiences"]} />,
          },
          {
            path: "wire_fx_rates",
            element: (
              <ValidatedRoutes
                validate={(userConfiguration) => {
                  const { permissions } = userConfiguration;
                  return permissions.includes("backoffice.view_fx_rates");
                }}
              />
            ),
            children: WIRE_FX_RATES_ROUTES,
          },
          {
            path: "guard",
            element: <ValidatedRoutes validate={canViewGuard} />,
            children: [
              {
                index: true,
                async lazy() {
                  const { GuardManager } = await import("./guard_manager/GuardManager");
                  return {
                    Component() {
                      const [searchParams] = useSearchParams();
                      return <GuardManager selectedId={searchParams.get("selectedId") ?? null} />;
                    },
                  };
                },
              },
            ],
          },
          {
            path: "analytics",
            element: <PermissionedRoutes requiredPermissions={["backoffice.view_reports"]} />,
            children: REPORTS_ROUTES,
          },
        ],
      },
    ],
  },
];
